/**
 * BB-swiper-init.js
 * Reusable Swiper initialization using data attributes
 */

(function () {
  'use strict';

  // Store all initialized Swiper instances
  window.BBSwiperInstances = window.BBSwiperInstances || new Map();

  /**
   * Parse a data attribute value to its proper type
   */
  function parseValue(value) {
    if (value === 'true') return true;
    if (value === 'false') return false;
    if (value === 'null') return null;
    if (value === 'auto') return 'auto';

    // Try to parse as number
    const num = Number(value);
    if (!isNaN(num) && value !== '') return num;

    // Try to parse as JSON (for objects/arrays)
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }

  /**
   * Convert kebab-case to camelCase
   */
  function kebabToCamel(str) {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  }

  /**
   * Extract Swiper configuration from data attributes
   */
  function getConfigFromDataAttributes(element) {
    const config = {};
    const dataset = element.dataset;

    // Map of data attribute names to Swiper option names
    const attributeMap = {
      // Core
      swiperSlidesPerView: 'slidesPerView',
      swiperSpaceBetween: 'spaceBetween',
      swiperSpeed: 'speed',
      swiperDirection: 'direction',
      swiperInitialSlide: 'initialSlide',
      swiperLoop: 'loop',
      swiperRewind: 'rewind',
      swiperCenteredSlides: 'centeredSlides',
      swiperCenteredSlidesBounds: 'centeredSlidesBounds',
      swiperSlidesPerGroup: 'slidesPerGroup',
      swiperGrabCursor: 'grabCursor',
      swiperWatchOverflow: 'watchOverflow',
      swiperWatchSlidesProgress: 'watchSlidesProgress',
      swiperAllowTouchMove: 'allowTouchMove',
      swiperSlideToClickedSlide: 'slideToClickedSlide',
      swiperFreeMode: 'freeMode',
      swiperFreeModeSticky: 'freeModeSticky',

      // Slides offset
      swiperSlidesOffsetBefore: 'slidesOffsetBefore',
      swiperSlidesOffsetAfter: 'slidesOffsetAfter',

      // Effects
      swiperEffect: 'effect',
      swiperFadeEffect: 'fadeEffect',
      swiperCubeEffect: 'cubeEffect',
      swiperCoverflowEffect: 'coverflowEffect',
      swiperFlipEffect: 'flipEffect',
      swiperCreativeEffect: 'creativeEffect',
      swiperCardsEffect: 'cardsEffect',

      // Autoplay
      swiperAutoplay: 'autoplay',
      swiperAutoplayDelay: 'autoplayDelay',
      swiperAutoplayDisableOnInteraction: 'autoplayDisableOnInteraction',
      swiperAutoplayPauseOnMouseEnter: 'autoplayPauseOnMouseEnter',
      swiperAutoplayReverseDirection: 'autoplayReverseDirection',
      swiperAutoplayStopOnLastSlide: 'autoplayStopOnLastSlide',

      // Navigation selectors (custom)
      swiperNavNext: 'navNext',
      swiperNavPrev: 'navPrev',
      swiperPaginationEl: 'paginationEl',
      swiperScrollbarEl: 'scrollbarEl',

      // Pagination
      swiperPagination: 'pagination',
      swiperPaginationType: 'paginationType',
      swiperPaginationClickable: 'paginationClickable',
      swiperPaginationDynamicBullets: 'paginationDynamicBullets',

      // Scrollbar
      swiperScrollbar: 'scrollbar',
      swiperScrollbarDraggable: 'scrollbarDraggable',
      swiperScrollbarHide: 'scrollbarHide',

      // Breakpoints (JSON string)
      swiperBreakpoints: 'breakpoints',

      // Mousewheel
      swiperMousewheel: 'mousewheel',
      swiperMousewheelForceToAxis: 'mousewheelForceToAxis',
      swiperMousewheelReleaseOnEdges: 'mousewheelReleaseOnEdges',
      swiperMousewheelSensitivity: 'mousewheelSensitivity',

      // Keyboard
      swiperKeyboard: 'keyboard',

      // Lazy loading
      swiperLazy: 'lazy',
      swiperLazyPreloadPrev: 'lazyPreloadPrev',
      swiperLazyPreloaderClass: 'lazyPreloaderClass',

      // A11y
      swiperA11y: 'a11y',

      // Touch
      swiperTouchRatio: 'touchRatio',
      swiperTouchAngle: 'touchAngle',
      swiperSimulateTouch: 'simulateTouch',
      swiperShortSwipes: 'shortSwipes',
      swiperThreshold: 'threshold',

      // Custom classes
      swiperSlideClass: 'slideClass',
      swiperSlideActiveClass: 'slideActiveClass',
      swiperWrapperClass: 'wrapperClass',

      // Pagination custom classes
      swiperPaginationBulletClass: 'paginationBulletClass',
      swiperPaginationBulletActiveClass: 'paginationBulletActiveClass',

      // Responsive control
      swiperMobileOnly: 'mobileOnly',
      swiperMobileBreakpoint: 'mobileBreakpoint',
      swiperDesktopOnly: 'desktopOnly',

      // Other
      swiperResizeObserver: 'resizeObserver',
      swiperObserver: 'observer',
      swiperObserveParents: 'observeParents',
      swiperObserveSlideChildren: 'observeSlideChildren',
      swiperPreventClicks: 'preventClicks',
      swiperPreventClicksPropagation: 'preventClicksPropagation',
      swiperNested: 'nested',

      // Custom: Rewind to start after reaching end (visible slide back animation)
      swiperRewindToStart: 'rewindToStart',
      swiperRewindToStartDelay: 'rewindToStartDelay',
    };

    // Process all data-swiper-* attributes
    for (const [dataKey, value] of Object.entries(dataset)) {
      if (dataKey.startsWith('swiper') && dataKey !== 'swiperInit') {
        const swiperOption = attributeMap[dataKey];
        if (swiperOption) {
          config[swiperOption] = parseValue(value);
        }
      }
    }

    return config;
  }

  /**
   * Find elements by selector, searching in element first, then parent containers
   */
  function findElements(element, selector) {
    const results = [];
    const selectors = selector.split(',').map((s) => s.trim());

    // Search in multiple parent levels to find all matching elements
    const searchContainers = [
      element,
      element.closest('[class*="swiper-container"]'),
      element.closest('[class*="container"]'),
      element.closest('section'),
      element.parentElement,
    ].filter(Boolean);

    // Use a Set to avoid duplicates
    const foundElements = new Set();

    searchContainers.forEach((container) => {
      if (!container) return;
      selectors.forEach((sel) => {
        const found = container.querySelectorAll(sel);
        found.forEach((el) => foundElements.add(el));
      });
    });

    return Array.from(foundElements);
  }

  /**
   * Build the final Swiper configuration object
   */
  function buildSwiperConfig(element, dataConfig) {
    const config = { ...dataConfig };

    // Handle navigation - support multiple selectors and search in parent containers
    let navNextEls = [];
    let navPrevEls = [];

    if (dataConfig.navNext) {
      // Custom selector provided
      const selectors = dataConfig.navNext.split(',').map((s) => s.trim());
      selectors.forEach((sel) => {
        navNextEls = navNextEls.concat(findElements(element, sel));
      });
    } else {
      // Default: look for standard swiper buttons in element and parent
      // Match: nav--next, nav--top-next, button-next, swiper-button-next
      navNextEls = findElements(element, '.swiper-button-next, [class*="-next"], [class$="-next"]');
    }

    if (dataConfig.navPrev) {
      // Custom selector provided
      const selectors = dataConfig.navPrev.split(',').map((s) => s.trim());
      selectors.forEach((sel) => {
        navPrevEls = navPrevEls.concat(findElements(element, sel));
      });
    } else {
      // Default: look for standard swiper buttons in element and parent
      // Match: nav--prev, nav--top-prev, button-prev, swiper-button-prev
      navPrevEls = findElements(element, '.swiper-button-prev, [class*="-prev"], [class$="-prev"]');
    }

    if (navNextEls.length > 0 || navPrevEls.length > 0) {
      config.navigation = {
        nextEl: navNextEls.length === 1 ? navNextEls[0] : navNextEls.length > 1 ? navNextEls : null,
        prevEl: navPrevEls.length === 1 ? navPrevEls[0] : navPrevEls.length > 1 ? navPrevEls : null,
      };
    }
    delete config.navNext;
    delete config.navPrev;

    // Handle pagination - search in parent containers too
    let paginationEl = null;
    if (dataConfig.paginationEl) {
      const found = findElements(element, dataConfig.paginationEl);
      paginationEl = found.length > 0 ? found[0] : null;
    } else {
      const found = findElements(element, '.swiper-pagination');
      paginationEl = found.length > 0 ? found[0] : null;
    }

    if (paginationEl || dataConfig.pagination === true) {
      config.pagination = {
        el: paginationEl,
        type: dataConfig.paginationType || 'bullets',
        clickable: dataConfig.paginationClickable !== false,
        dynamicBullets: dataConfig.paginationDynamicBullets || false,
      };

      // Add custom bullet classes if provided
      if (dataConfig.paginationBulletClass) {
        config.pagination.bulletClass = dataConfig.paginationBulletClass;
      }
      if (dataConfig.paginationBulletActiveClass) {
        config.pagination.bulletActiveClass = dataConfig.paginationBulletActiveClass;
      }
    }
    delete config.paginationEl;
    delete config.paginationType;
    delete config.paginationClickable;
    delete config.paginationDynamicBullets;
    delete config.paginationBulletClass;
    delete config.paginationBulletActiveClass;

    // Handle scrollbar - search in parent containers too
    let scrollbarEl = null;
    if (dataConfig.scrollbarEl) {
      const found = findElements(element, dataConfig.scrollbarEl);
      scrollbarEl = found.length > 0 ? found[0] : null;
    } else {
      const found = findElements(element, '.swiper-scrollbar');
      scrollbarEl = found.length > 0 ? found[0] : null;
    }

    if (scrollbarEl || dataConfig.scrollbar === true) {
      config.scrollbar = {
        el: scrollbarEl,
        draggable: dataConfig.scrollbarDraggable !== false,
        hide: dataConfig.scrollbarHide || false,
      };
    }
    delete config.scrollbarEl;
    delete config.scrollbarDraggable;
    delete config.scrollbarHide;

    // Handle autoplay
    if (dataConfig.autoplay) {
      const autoplayValue = dataConfig.autoplay;
      if (autoplayValue === true || typeof autoplayValue === 'number') {
        config.autoplay = {
          delay: typeof autoplayValue === 'number' ? autoplayValue : dataConfig.autoplayDelay || 3000,
          disableOnInteraction:
            dataConfig.autoplayDisableOnInteraction !== undefined ? dataConfig.autoplayDisableOnInteraction : true,
          pauseOnMouseEnter: dataConfig.autoplayPauseOnMouseEnter || false,
          reverseDirection: dataConfig.autoplayReverseDirection || false,
          stopOnLastSlide: dataConfig.autoplayStopOnLastSlide || false,
        };
      }
    }
    delete config.autoplayDelay;
    delete config.autoplayDisableOnInteraction;
    delete config.autoplayPauseOnMouseEnter;
    delete config.autoplayReverseDirection;
    delete config.autoplayStopOnLastSlide;

    // Handle fade effect
    if (dataConfig.effect === 'fade') {
      config.fadeEffect = dataConfig.fadeEffect || { crossFade: true };
    }

    // Handle freeMode
    if (dataConfig.freeMode === true) {
      config.freeMode = {
        enabled: true,
        sticky: dataConfig.freeModeSticky || false,
      };
    }
    delete config.freeModeSticky;

    // Handle mousewheel
    if (dataConfig.mousewheel === true) {
      config.mousewheel = {
        enabled: true,
        forceToAxis: dataConfig.mousewheelForceToAxis !== false,
        releaseOnEdges: dataConfig.mousewheelReleaseOnEdges || false,
        sensitivity: dataConfig.mousewheelSensitivity || 1,
      };
    }
    delete config.mousewheelForceToAxis;
    delete config.mousewheelReleaseOnEdges;
    delete config.mousewheelSensitivity;

    // Handle keyboard
    if (dataConfig.keyboard === true) {
      config.keyboard = {
        enabled: true,
        onlyInViewport: true,
      };
    }

    // Handle rewindToStart - wrap to first/last slide when at edges
    // When at end and clicking next or swiping left -> go to first slide
    // When at beginning and clicking prev or swiping right -> go to last slide
    if (dataConfig.rewindToStart === true) {
      // Disable Swiper's built-in navigation so we handle it ourselves
      config.navigation = false;
      config.allowSlideNext = true;
      config.allowSlidePrev = true;

      config.on = config.on || {};

      config.on.init = function (swiper) {
        // Find navigation buttons in parent container
        const container = swiper.el.closest('section') || swiper.el.parentElement;
        const nextBtns = container.querySelectorAll('[class*="--next"], [class*="-next"], .swiper-button-next');
        const prevBtns = container.querySelectorAll('[class*="--prev"], [class*="-prev"], .swiper-button-prev');

        // Throttle to prevent rapid clicks/swipes causing glitches
        let isTransitioning = false;
        const transitionDelay = 250; // ms to wait between actions

        const throttledAction = function (action) {
          if (isTransitioning) return;
          isTransitioning = true;
          action();
          setTimeout(function () {
            isTransitioning = false;
          }, transitionDelay);
        };

        // Handle next buttons
        nextBtns.forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            throttledAction(function () {
              if (swiper.isEnd) {
                // At end, wrap to first
                swiper.slideTo(0);
              } else {
                swiper.slideNext();
              }
            });
          });
        });

        // Handle prev buttons
        prevBtns.forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            throttledAction(function () {
              if (swiper.isBeginning) {
                // At beginning, wrap to last
                swiper.slideTo(swiper.slides.length - 1);
              } else {
                swiper.slidePrev();
              }
            });
          });
        });

        // Handle swipe gestures at edges
        let touchStartX = 0;
        let wasAtEnd = false;
        let wasAtBeginning = false;
        const swipeThreshold = 50;

        swiper.on('touchStart', function () {
          touchStartX = swiper.touches.startX;
          // Remember edge state at start of swipe
          wasAtEnd = swiper.isEnd;
          wasAtBeginning = swiper.isBeginning;
        });

        swiper.on('touchEnd', function () {
          const touchEndX = swiper.touches.currentX;
          const diff = touchEndX - touchStartX;

          // Use small delay to let swiper finish its transition, then correct if needed
          setTimeout(function () {
            if (wasAtEnd && diff < -swipeThreshold) {
              // Swiped left while at end -> go to first
              throttledAction(function () {
                swiper.slideTo(0);
              });
            } else if (wasAtBeginning && diff > swipeThreshold) {
              // Swiped right while at beginning -> go to last
              throttledAction(function () {
                swiper.slideTo(swiper.slides.length - 1);
              });
            }
          }, 10);
        });
      };
    }
    delete config.rewindToStart;
    delete config.rewindToStartDelay;

    return config;
  }

  /**
   * Check if swiper should be active based on responsive settings
   */
  function shouldInitialize(element) {
    const dataset = element.dataset;
    const mobileOnly = dataset.swiperMobileOnly === 'true';
    const desktopOnly = dataset.swiperDesktopOnly === 'true';
    const mobileBreakpoint = parseInt(dataset.swiperMobileBreakpoint, 10) || 800;

    const isMobile = window.innerWidth <= mobileBreakpoint;

    if (mobileOnly && !isMobile) return false;
    if (desktopOnly && isMobile) return false;

    return true;
  }

  /**
   * Initialize a single Swiper instance
   */
  function initSwiper(element) {
    if (typeof Swiper === 'undefined') {
      console.warn('BB-swiper-init: Swiper library not loaded');
      return null;
    }

    // Check responsive conditions
    if (!shouldInitialize(element)) {
      // Destroy if exists and shouldn't be active
      if (element.swiper || window.BBSwiperInstances.has(element)) {
        destroySwiper(element);
      }
      return null;
    }

    // Skip if already initialized
    if (element.swiper || window.BBSwiperInstances.has(element)) {
      return element.swiper || window.BBSwiperInstances.get(element);
    }

    try {
      const dataConfig = getConfigFromDataAttributes(element);
      const config = buildSwiperConfig(element, dataConfig);

      // Remove responsive control options from config
      delete config.mobileOnly;
      delete config.mobileBreakpoint;
      delete config.desktopOnly;

      // Add default observer settings for dynamic content
      if (config.observer === undefined) {
        config.observer = true;
      }
      if (config.observeParents === undefined) {
        config.observeParents = true;
      }

      // Default to moving one slide at a time
      if (config.slidesPerGroup === undefined) {
        config.slidesPerGroup = 1;
      }

      console.log('BB-swiper-init: Initializing swiper with config', config);

      const swiperInstance = new Swiper(element, config);
      window.BBSwiperInstances.set(element, swiperInstance);

      console.log('BB-swiper-init: Swiper initialized successfully', swiperInstance);

      // Dispatch custom event for additional customization
      element.dispatchEvent(
        new CustomEvent('swiper:init', {
          detail: { swiper: swiperInstance, config },
          bubbles: true,
        })
      );

      return swiperInstance;
    } catch (error) {
      console.error('BB-swiper-init: Failed to initialize Swiper', error);
      return null;
    }
  }

  /**
   * Destroy a Swiper instance
   */
  function destroySwiper(element) {
    const instance = window.BBSwiperInstances.get(element) || element.swiper;
    if (instance) {
      instance.destroy(true, true);
      window.BBSwiperInstances.delete(element);
    }
  }

  /**
   * Initialize all Swipers with data-swiper-init attribute
   */
  function initAllSwipers() {
    const swiperElements = document.querySelectorAll('[data-swiper-init]');
    console.log('BB-swiper-init: Found', swiperElements.length, 'elements with data-swiper-init');
    swiperElements.forEach((element) => {
      initSwiper(element);
    });
  }

  /**
   * Get a Swiper instance by element
   */
  function getSwiperInstance(element) {
    return window.BBSwiperInstances.get(element) || element.swiper || null;
  }

  // Expose API globally
  window.BBSwiper = {
    init: initSwiper,
    initAll: initAllSwipers,
    destroy: destroySwiper,
    getInstance: getSwiperInstance,
    instances: window.BBSwiperInstances,
  };

  /**
   * Handle resize for responsive swipers
   */
  let resizeTimer;
  function handleResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      const swiperElements = document.querySelectorAll('[data-swiper-init]');
      swiperElements.forEach((element) => {
        // Check if has responsive settings
        if (element.dataset.swiperMobileOnly === 'true' || element.dataset.swiperDesktopOnly === 'true') {
          initSwiper(element); // Will init or destroy based on screen size
        }
      });
    }, 250);
  }

  /**
   * Wait for Swiper to be available, then initialize
   */
  function waitForSwiperAndInit() {
    if (typeof Swiper !== 'undefined') {
      initAllSwipers();
    } else {
      // Swiper not loaded yet, wait and retry
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max
      const checkInterval = setInterval(function () {
        attempts++;
        if (typeof Swiper !== 'undefined') {
          clearInterval(checkInterval);
          initAllSwipers();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          console.warn('BB-swiper-init: Swiper library not found after waiting');
        }
      }, 100);
    }
  }

  // Auto-initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForSwiperAndInit);
  } else {
    // DOM already loaded
    waitForSwiperAndInit();
  }

  // Handle resize for responsive swipers (mobile-only, desktop-only)
  window.addEventListener('resize', handleResize);

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target;
    const swiperElements = section.querySelectorAll('[data-swiper-init]');
    swiperElements.forEach((element) => {
      initSwiper(element);
    });
  });

  // Cleanup on Shopify section unload
  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target;
    const swiperElements = section.querySelectorAll('[data-swiper-init]');
    swiperElements.forEach((element) => {
      destroySwiper(element);
    });
  });
})();
